import React, { useEffect } from "react";
import uploadImage from "assets/images/file-upload-icon.svg";
import deleteIcon from "assets/images/icons/delete-rounded-red.svg";
import { useDropzone } from "react-dropzone";
import parse from "html-react-parser";
import { fileNameTrimmer } from "utils";
import { useTaskFileUploadContext } from "../hooks";

const UPLOAD_TYPE = 3;

export const FileUploader = ({
  currentCourseTask,
  title,
  description,
  taskExtendedData,
  isLoading,
}) => {
  const {
    id: studentTaskId,
    studentTaskStatus,
    courseTaskId,
    taskGroup,
  } = currentCourseTask;

  const {
    setFileUploadState,
    uploadingFiles,
    uploadFile,
    uploadStudentTaskFile,
    supportedExtensions,
    cancelUpload,
    deleteFile,
    deletingId,
  } = useTaskFileUploadContext();

  const { allowMultipleUploads, extensions, fileUploads } =
    !isLoading && taskExtendedData;

  useEffect(() => {
    if (!!extensions && extensions?.length !== 0) {
      setFileUploadState((draft) => {
        draft.extensions = extensions || [];
        draft.studentTaskId = studentTaskId;
        draft.courseTaskId = courseTaskId;
        draft.taskGroup = taskGroup;
        return draft;
      });
    }
  }, [extensions]);

  const isCompletedTask = studentTaskStatus === 2;

  const isUploadAllowed =
    uploadingFiles?.length === 0 && fileUploads?.length === 0;
  const isDisabledUpload = !allowMultipleUploads && !isUploadAllowed;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (
        !allowMultipleUploads &&
        !isUploadAllowed &&
        !acceptedFiles.length === 0
      ) {
        return;
      }

      acceptedFiles.forEach((file) => {
        // Begin upload
        uploadFile(file, UPLOAD_TYPE).then((result) => {
          if (result?.originalName && result?.filePath) {
            const formData = new FormData();
            formData.append("fileName", result.originalName);
            formData.append("filePath", result.filePath);

            uploadStudentTaskFile.mutate({ studentTaskId, formData });
          }
        });
      });
    },

    multiple: allowMultipleUploads,
    disabled: isDisabledUpload,
  });

  return (
    <div className="file-uploading-section">
      <div className="file-uploader-header">
        {title && <h2>{title}</h2>}
        {description && <p>{parse(description)}</p>}
      </div>
      <div className="file-upload-controller">
        {!isCompletedTask && (
          <>
            <div
              {...getRootProps({ className: "dropzone" })}
              className={`dropZone ${isDragActive ? "drop-active" : ""} ${
                isDisabledUpload ? "field-disabled" : ""
              }`}
            >
              <div>
                <input {...getInputProps()} />
                <div style={{ marginBottom: 0 }}>
                  <div className="upload-icon">
                    <img src={uploadImage} />
                  </div>
                  {isDragActive ? (
                    <span className="upload-label-text">
                      Drop the files here ...
                    </span>
                  ) : (
                    <>
                      <span className="upload-label-text">
                        Drag & drop files or
                        <span className="bold-blue">Browse</span>
                      </span>
                      <div className="upload-sub-text">
                        {`Supported formats: ${supportedExtensions?.join(",")}`}
                      </div>
                      {!allowMultipleUploads && (
                        <div className="upload-sub-text">
                          Only one file is allowed. Please select a single file
                          to upload.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {uploadingFiles?.length !== 0 && !isCompletedTask && (
          <div className="upload-file-list-block file-inprogress">
            <h4>Uploading</h4>
            <div className="upload-file-list">
              {uploadingFiles?.map(({ id, name, progress, error }) => (
                <div
                  key={name}
                  className={`upload-file__item ${error ? "error" : ""}`}
                >
                  <div className="upload-file__info">
                    <span>{fileNameTrimmer(name)}</span>
                    <button
                      className="upload-file__action action__remove"
                      onClick={() => cancelUpload(id)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8.40488 16C6.39287 16 4.46303 15.1571 3.04041 13.6568C1.61769 12.1565 0.818359 10.1217 0.818359 8C0.818359 5.87833 1.61769 3.84332 3.04041 2.34315C4.46324 0.842902 6.39287 0 8.40488 0C10.4169 0 12.3467 0.842902 13.7693 2.34315C15.1921 3.84353 15.9914 5.87833 15.9914 8C15.989 10.121 15.189 12.1545 13.7667 13.654C12.3444 15.1538 10.416 15.9974 8.40488 16ZM11.3305 5.94242V5.9423C11.4635 5.80678 11.5394 5.62063 11.541 5.42558C11.5426 5.23053 11.4699 5.04292 11.3391 4.90496C11.2082 4.76701 11.0303 4.69031 10.8454 4.69202C10.6604 4.69372 10.4839 4.77371 10.3553 4.91397L8.40488 6.97149L6.45441 4.91397C6.27925 4.7356 6.02789 4.66779 5.79293 4.73572C5.55809 4.80366 5.37463 4.99713 5.31019 5.24477C5.24577 5.49254 5.31008 5.7576 5.47923 5.9423L7.4297 7.99983L5.47923 10.0573C5.34622 10.1929 5.27036 10.379 5.26874 10.5741C5.26713 10.7691 5.33987 10.9567 5.47068 11.0947C5.6015 11.2326 5.77942 11.3093 5.96439 11.3076C6.14936 11.3059 6.32588 11.2259 6.4544 11.0857L8.40487 9.02815L10.3553 11.0857C10.4838 11.2259 10.6604 11.3059 10.8454 11.3076C11.0303 11.3093 11.2082 11.2326 11.3391 11.0947C11.4699 10.9567 11.5426 10.7691 11.541 10.5741C11.5394 10.379 11.4635 10.1929 11.3305 10.0573L9.38004 7.99983L11.3305 5.94242Z"
                          fill={!error ? "#E6E6E6" : "#E41D1D"}
                        />
                      </svg>
                    </button>
                  </div>
                  {!error && (
                    <div className="progress-bar__thin">
                      <div
                        className="progress-bar__fill"
                        style={{
                          width: `${progress}%`,
                          transition: "width 0.3s",
                        }}
                      ></div>
                    </div>
                  )}
                  {error && <div className="upload-error-text">{error}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {fileUploads?.length !== 0 && (
          <div className="upload-file-list-block file-uploaded">
            <h4>Uploaded</h4>
            <div className="upload-file-list">
              {fileUploads?.map(({ id, fileName }) => (
                <div className="upload-file__item" key={fileName}>
                  <div key={fileName} className="upload-file__info">
                    <span>{fileNameTrimmer(fileName)}</span>
                    {!isCompletedTask && (
                      <button
                        className="upload-file__action action__delete"
                        onClick={() => deleteFile(id)}
                        disabled={deletingId === id}
                      >
                        <img src={deleteIcon} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
